"use client";

import React from "react";
import { Sparkles, ShoppingBag, Wallet, CreditCard } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * CreateWebshop — Section 3: Demo-Shop-Mockup
 * Drop-in für: src/pages/CreateWebshop/sections/DemoShopMockup.tsx
 *
 * Stilisierter Shop-Mockup im Browser-Frame mit fiktivem Produkt-Grid +
 * Twint/Stripe-Checkout-Indikator. Kein echtes Webshop-Demo-Bild benötigt —
 * wir rendern einen optisch sauberen Beispiel-Shop nativ.
 */

const products = [{
  name: "Bio-Honig 500g",
  price: "CHF 18",
  emoji: "🍯"
}, {
  name: "Berg-Käse Premium",
  price: "CHF 32",
  emoji: "🧀"
}, {
  name: "Schweizer Schokolade",
  price: "CHF 14",
  emoji: "🍫"
}, {
  name: "Bauern-Brot Laib",
  price: "CHF 9",
  emoji: "🍞"
}];
export default function DemoShopMockup() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="demo-shop" className="bg-background py-20 md:py-24 overflow-hidden">
      <div ref={ref} className={`max-w-6xl mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            Beispiel-Shop
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            So sieht Ihr Webshop aus
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Produkte, Warenkorb, Checkout mit Twint und Stripe — alles ready.
          </p>
        </div>

        {/* Browser-Frame mit fiktivem Shop */}
        <div className="max-w-4xl mx-auto bg-foreground rounded-t-2xl rounded-b-md p-2.5 shadow-2xl">
          {/* Browser chrome */}
          <div className="bg-secondary rounded-t-md px-3 py-2 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-300" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
            </div>
            <div className="flex-1 mx-3 bg-background rounded px-3 py-0.5 text-[11px] text-muted-foreground font-medium truncate">
              alpenshop.ch
            </div>
          </div>

          {/* Shop-Layout */}
          <div className="bg-background rounded-b p-6 md:p-10">
            {/* Shop-Header */}
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary text-white flex items-center justify-center font-bold">
                  A
                </div>
                <span className="font-bold text-foreground">Alpenshop</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShoppingBag className="w-4 h-4" />
                <span>Warenkorb (2)</span>
              </div>
            </div>

            {/* Produkt-Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {products.map(p => <div key={p.name} className="bg-secondary rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="aspect-square bg-background rounded-md flex items-center justify-center text-4xl mb-3 border border-border">
                    {p.emoji}
                  </div>
                  <div className="text-sm font-semibold text-foreground mb-1 truncate">
                    {p.name}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-primary">
                      {p.price}
                    </span>
                    <button className="text-[10px] bg-primary text-white px-2 py-1 rounded">
                      + Korb
                    </button>
                  </div>
                </div>)}
            </div>

            {/* Checkout-Indikator */}
            <div className="bg-secondary border border-border rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Zahlung möglich mit:
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-foreground bg-background border border-border rounded px-2 py-1">
                    <Wallet className="w-3 h-3 text-red-600" /> Twint
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-foreground bg-background border border-border rounded px-2 py-1">
                    <CreditCard className="w-3 h-3 text-blue-600" /> Stripe
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Apple Pay · Google Pay
                  </span>
                </div>
              </div>
              <button className="bg-primary text-white text-xs font-medium px-4 py-2 rounded-md shadow shadow-primary/20">
                Zur Kasse
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground italic mt-8">
          Beispiel-Layout — Ihr Shop wird auf Ihre Branche und Produkte
          zugeschnitten.
        </p>
      </div>
    </section>;
}